import { api, type Asset, type AssetKind, type InitUploadResp } from "../api.js";

/**
 * Pipeline d'upload d'un fichier vers Bunny (vidéo) ou R2 (audio/PDF/image).
 *
 * Étapes :
 *   1. POST /assets/init-upload — crée Asset (isReady=false) + URL signée
 *   2. PUT direct vers le provider depuis le navigateur
 *   3. POST /assets/:id/confirm — marque Asset.isReady=true
 *
 * onProgress reçoit un nombre entre 0 et 1.
 */
export async function uploadAsset(
  file: File,
  opts: { onProgress?: (pct: number) => void } = {}
): Promise<Asset> {
  const kind = detectKind(file);
  if (!kind) throw new Error(`Type de fichier non supporté : ${file.type}`);

  // 1. Init
  const init = await api.post<InitUploadResp>("/assets/init-upload", {
    name: file.name,
    kind,
    mimeType: file.type || "application/octet-stream",
    sizeBytes: file.size,
  });

  // 2. Upload direct vers Bunny / R2
  // En mode dev (clés non configurées), l'URL est factice → on simule l'upload.
  if (init.upload.dev) {
    // Mime une progression visuelle pour le feedback utilisateur
    if (opts.onProgress) {
      for (let p = 0.1; p <= 1; p += 0.1) {
        await new Promise((r) => setTimeout(r, 80));
        opts.onProgress(p);
      }
    }
  } else {
    await putWithProgress(init.upload.url, file, init.upload.headers, opts.onProgress);
  }

  // 3. Confirm
  const finalAsset = await api.post<Asset>(`/assets/${init.asset.id}/confirm`);
  return finalAsset;
}

function detectKind(file: File): AssetKind | null {
  const t = file.type || "";
  if (t.startsWith("video/")) return "VIDEO";
  if (t.startsWith("audio/")) return "AUDIO";
  if (t === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) return "PDF";
  if (t.startsWith("image/")) return "IMAGE";
  return null;
}

function putWithProgress(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress?: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload échoué (HTTP ${xhr.status}) : ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error("Erreur réseau pendant l'upload"));
    xhr.send(body);
  });
}

export function humanSize(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

export function humanDuration(sec: number | null): string {
  if (sec === null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export const KIND_LABEL: Record<AssetKind, string> = {
  VIDEO: "Vidéo",
  AUDIO: "Audio",
  PDF: "PDF",
  IMAGE: "Image",
};

export const KIND_ICON: Record<AssetKind, string> = {
  VIDEO: "▶",
  AUDIO: "♫",
  PDF: "📄",
  IMAGE: "🖼",
};
